import * as React from 'react';
import * as _ from 'lodash';
import * as styles from '../../help/help.module.scss';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { TextTransformProperty, OverflowYProperty, PositionProperty } from 'csstype';
import { Redirect } from 'react-router-dom';
import { Scrollbars } from 'react-custom-scrollbars';
import useMeasuredRef from '../../../hooks/useMeasuredRef';
import Paths from '../../../models/enums/paths';
import useHelpDocuments from '../../../hooks/useHelpDocuments';
import SidePanel from './help-side-panel';
import useHelp from '../../../hooks/useHelp';
import { ThemeProvider, createTheme } from '@material-ui/core';

interface Props {
  searchQuery: string;
  isSearching: boolean;
  setIsSearching: (searching: boolean) => void;
}

const dialogStyles = {
  helpDocuments: { width: '100%', border: '1px solid #dadada', overflowY: 'scroll' as OverflowYProperty },
  contentHeader: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px 0 30px',
    fontWeight: '600' as 'bold',
    fontSize: '16px',
    borderBottom: '1px solid #8a8a8a',
    justifyContent: 'space-between'
  },
  fullScreenDiv: {
    color: '#006da8',
    fontWeight: '400' as 'normal',
    fontSize: '14px',
    textTransform: 'uppercase' as TextTransformProperty,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  fullScreenText: {
    marginLeft: '2px'
  },
  documentsContainer: {
    margin: '48px 57px',
    position: 'relative' as PositionProperty
  }
};

const materialTheme = createTheme({
  overrides: {
    MuiDialog: {
      root: {
        zIndex: '20000 !important'
      }
    }
  }
});

/**
 * FullScreenDialog
 * @function FullScreenDialog - Full screen dialog to show the contents of a help document
 */
const FullScreenDialog = ({ openFullScreen, setOpenFullScreen, isSearching }) => {
  const { renderHelpContent } = useHelpDocuments();

  return (
    <ThemeProvider theme={materialTheme}>
      <Dialog fullScreen open={openFullScreen} onClose={() => setOpenFullScreen(false)}>
        <DialogContent>
          <div style={dialogStyles.helpDocuments}>
            <div style={dialogStyles.contentHeader}>
              <span>Document</span>
              <div onClick={() => setOpenFullScreen(false)} style={dialogStyles.fullScreenDiv}>
                <FullscreenExitIcon />
                <span style={dialogStyles.fullScreenText}>Minimize</span>
              </div>
            </div>
            <div style={dialogStyles.documentsContainer}>{renderHelpContent(isSearching)}</div>
          </div>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

/**
 * HelpSection
 * @function HelpSection - Component for the left toolbar and the help documents page
 */
const HelpSection = ({ searchQuery, isSearching, setIsSearching }: Props) => {
  const { helpDocuments, activeDocumentId, renderHelpContent, renderPreviews, dispatch } = useHelpDocuments();
  const [openFullScreen, setOpenFullScreen] = React.useState<boolean>(false);
  const [redirect, setRedirect] = React.useState<boolean>(false);
  const { documentsWithRoot } = useHelp();

  const { measuredRef, dimensions } = useMeasuredRef([], true);

  // If there is no active document, the first document will become the active document so that there is always a document to display
  React.useEffect(() => {
    if (helpDocuments.rootId) {
      if (!activeDocumentId) {
        dispatch({
          activeDocumentId: documentsWithRoot(helpDocuments).find(doc => doc.id === '-1').children[0]
        });
      } else if (activeDocumentId && !redirect) {
        setRedirect(true);
      }
    }
  }, [activeDocumentId, helpDocuments.rootId]);

  // Redirects to the first document from the document list if there is no active document
  if (redirect) {
    setRedirect(false);
    return <Redirect to={`${Paths.ClientHelpDocuments}/${activeDocumentId}`} />;
  }

  /** Array of JSX elements that displays the preview of the help documents as the user is typing in the search bar */
  const previews = renderPreviews(searchQuery, isSearching, setIsSearching);

  return (
    <div
      className={styles.helpContent}
      ref={measuredRef}
      style={{ height: `calc(100vh - ${dimensions ? 20 + dimensions.top : 0}px` }}
    >
      <div className={styles.tableOfContents}>
        <h5 className={styles.tableHeader}>Select a Document</h5>
        <div className={styles.folders}>
          <Scrollbars className={styles.scrollTableOfContents} autoHide={true} universal={true}>
            <SidePanel documentsList={helpDocuments} setIsSearching={setIsSearching} />
          </Scrollbars>
        </div>
      </div>
      <div className={styles.helpDocuments}>
        <div className={styles.contentHeader}>
          <h5>Document</h5>
          <div onClick={() => setOpenFullScreen(true)} className={styles.fullScreenText}>
            <FullscreenIcon />
            <span>Maximize</span>
          </div>
        </div>
        <div className={styles.documentsContainer}>
          {previews ? (
            previews
          ) : (
            <Scrollbars className={styles.scrolling} autoHide={true} universal={true} hideTracksWhenNotNeeded={true}>
              {renderHelpContent(isSearching)}
            </Scrollbars>
          )}
        </div>
        <FullScreenDialog
          openFullScreen={openFullScreen}
          setOpenFullScreen={setOpenFullScreen}
          isSearching={isSearching}
        />
      </div>
    </div>
  );
};

export default HelpSection;
